import React, {
    ChangeEvent,
    createElement,
    FC,
    memo,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import classNames from "classnames";

import StickyButton from "@/common/StickyButton";
import { logAnalyticsEvent, setAnalyticsUserProperties } from "@/constants/amplitude";
import {
    defaultMaleBodyFatRangeValues,
    FemaleImagesByBodyFatRange,
    ImagesByBodyFatRange,
} from "@/constants/bodyMetrics";
import { BackendBodyTypes } from "@/constants/bodyType";
import { WebEventProps, WebEventTypes } from "@/constants/event";
import { QUESTIONS_IDS, QuestionTypes } from "@/constants/questionsIds";
import usePreloadImg from "@/hooks/usePreloadImg";
import { ALLOW_X_SWIPE_CLASS_NAMES } from "@/hooks/useSwipe";
import i18n from "@/localization/i18next";
import { AnswerVariantTypeByQuestionType } from "@/types/answers";
import { Question } from "@/types/question";
import { Answers } from "@/utils/AnswersProvider";
import { useGender } from "@/utils/GenderProvider";
import useAnswers from "@/utils/useAnswers";

type RangeAnswer = AnswerVariantTypeByQuestionType[QuestionTypes.Range];

const getRangeOptionValue = (rangesArray: RangeAnswer[], value: string) =>
    rangesArray.find((range) => range.value === value) ?? rangesArray[0];

const getDefaultRangeValue = (
    questionId: string,
    rangesArray: RangeAnswer[],
    answers: Answers,
    isFemale: boolean
): RangeAnswer => {
    if (answers[questionId]) {
        return getRangeOptionValue(rangesArray, answers[questionId] as string);
    } else if (questionId === QUESTIONS_IDS.BODY_FAT && !isFemale) {
        type BodyTypes =
            | BackendBodyTypes.Skinny
            | BackendBodyTypes.Regular
            | BackendBodyTypes.Extra;
        const bodyType = answers[QUESTIONS_IDS.BODY_TYPE] as BodyTypes;
        const defaultValue = defaultMaleBodyFatRangeValues?.[bodyType];

        return getRangeOptionValue(rangesArray, defaultValue);
    } else {
        return rangesArray[0];
    }
};

interface QuestionProps {
    question: Question<QuestionTypes.Range>;
    handleNext(answ: unknown): void;
    isActive: boolean;
}

const THUMB_SIZE = 12 + 2; // width + border;
const OFFSET_KOEF = 0.7;

const Range: FC<QuestionProps> = ({ question, handleNext, isActive }) => {
    const answersProps = useAnswers();
    const answers = answersProps.answers;
    const { isFemale } = useGender();

    const rangesArray = useMemo(() => Object.values(question.answers), [question.answers]);
    const defaultValue = useMemo(
        () => getDefaultRangeValue(question.id, rangesArray, answers, isFemale),
        [question.id, rangesArray, answers, isFemale]
    );
    const [selectedValue, setSelectedValue] = useState<RangeAnswer>(defaultValue);

    const [isDisabledButton, setIsDisabledButton] = useState(false);
    const tooltipRef = useRef<HTMLDivElement | null>(null);

    const valueIndex = useMemo(
        () => rangesArray.findIndex(({ value }) => value === selectedValue.value),
        [rangesArray, selectedValue.value]
    );

    useEffect(() => {
        if (tooltipRef.current) {
            const ratio = (valueIndex * 100) / (rangesArray.length - 1);

            tooltipRef.current.style.left = `calc(${ratio}% + (${
                THUMB_SIZE / 2 - ratio * OFFSET_KOEF
            }px))`;
        }
    }, [valueIndex, rangesArray.length]);

    useEffect(() => {
        if (isActive) {
            setIsDisabledButton(false);
        }
    }, [isActive]);

    useEffect(() => {
        if (isActive && !answers[question.id]) {
            setSelectedValue(defaultValue);
        }
    }, [isActive, defaultValue, question.id, rangesArray, answers]);

    const titleElement = useMemo(() => {
        const title = question?.title;

        if (typeof title === "function") {
            return createElement(title, { answers: answersProps });
        }

        return title;
    }, [answersProps, question]);

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        e.preventDefault();
        setSelectedValue(rangesArray[+e.target.value]);
    };

    const goToNextQuestion = (value: string) => {
        handleNext({ [question.id]: value });
        logAnalyticsEvent(WebEventTypes.RESULT_EVENT, {
            [WebEventProps.SCREEN]: question.event,
            [WebEventProps.RESULT]: value,
        });
        setAnalyticsUserProperties({ [question.id]: value });
    };

    const handleContinue = () => {
        if (selectedValue) {
            setIsDisabledButton(true);
            setSelectedValue(selectedValue);
            goToNextQuestion(selectedValue.value);
        }
    };

    const imagesToPreload = useMemo(() => {
        if (question.id === QUESTIONS_IDS.BODY_FAT) {
            return isFemale
                ? Object.values(FemaleImagesByBodyFatRange)
                : Object.values(ImagesByBodyFatRange);
        }
        return [];
    }, [isFemale, question.id]);
    usePreloadImg(imagesToPreload);

    return (
        <div className="flex h-full flex-col justify-end gap-6 sm:justify-between">
            <div className="flex h-full flex-col justify-center gap-12 sm:justify-start sm:gap-10">
                <div className="flex justify-center rounded-3xl bg-zing-neutral-cool-grey-800 bg-center female:bg-[#F3F4F7]">
                    <img
                        src={selectedValue.img}
                        className="h-[218px] self-end object-contain sm:h-[280px]"
                        alt=""
                    />
                </div>
                <div className="grid gap-6">
                    <div className="grid gap-4">
                        <div
                            className={classNames(
                                "whitespace-pre-line text-zing-24-32 font-semibold text-white female:text-black"
                            )}
                        >
                            {titleElement}
                        </div>
                        <div className="relative h-[90px]">
                            <input
                                name="range"
                                type="range"
                                className={classNames(
                                    "absolute bottom-[28px] h-2 w-full appearance-none bg-transparent bg-zing-warm-yellow-500 px-5 md:z-20",
                                    ...ALLOW_X_SWIPE_CLASS_NAMES
                                )}
                                min={0}
                                max={7}
                                value={valueIndex}
                                onChange={handleChange}
                            />
                            <div
                                ref={tooltipRef}
                                className="zing-tooltip-grey-700 absolute top-0 flex w-[60px] justify-center rounded-lg bg-zing-neutral-cool-grey-700 py-1.5 text-zing-12-18 font-semibold text-white female:bg-zing-warm-tangerine-500"
                            >
                                {selectedValue.value}%
                            </div>
                            <div className="absolute bottom-0 z-10 grid w-full gap-2.5">
                                <div className="flex h-2 justify-between rounded-3xl bg-gradient-to-r from-zing-warm-yellow-500 to-zing-warm-tangerine-500">
                                    {rangesArray.map((value, i) => (
                                        <div key={i} className="w-full"></div>
                                    ))}
                                </div>

                                <div className="flex justify-between text-white female:text-black">
                                    <span className="text-zing-12-18">{rangesArray[0].value}%</span>
                                    <span className="text-zing-12-18">
                                        {rangesArray[rangesArray.length - 1].value}%
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <StickyButton isDisabled={isDisabledButton} onClick={handleContinue}>
                {i18n.t("continue")}
            </StickyButton>
        </div>
    );
};

export default memo(Range);
